import type { DynamicInvoiceMeasurementChartMeter,
  DynamicInvoiceMeasurementChartMeterUsage,
  DynamicInvoiceMeterUsage } from '@weft/api-definition';
import type { LineItemProductVariant, Measurement, Period } from '@weft/types';
import { buildPeriod, dayjs,
  entries,
  extract,
  getOptionalObjectProperty,
  getPeriodDaysWithUtc, isDateBetween,
  isNullOrUndefined,
  keys,
  purge } from '@weft/utils';
import type { Dayjs } from '@weft/utils';
import type { ChartData } from 'chart.js';
import { newVyPalette } from '../../../assets/themes/new-vy/palette';
import { barColors } from './dynamic-invoice-view-page-consts/dynamic-invoice-usage-line-item-bar-chart-options';

export function getFormattedDate(date: Date): string {
  return dayjs(date).utcOffset(0).startOf('day').format('DD/MM');
}

export function getMeasurementFormattedStartTime(measurement: Measurement): string {
  return getFormattedDate(measurement.period.startTime);
}

export function generateDatasets(
  metersData: DynamicInvoiceMeasurementChartMeter,
  perUnitLineItemProductVariants: LineItemProductVariant[],
  billingCycle: Period,
  selectedInstanceKeyValue: string | undefined) {
  return entries(metersData).map(([meterId, meterUsageData], index) => {
    return {
      label: perUnitLineItemProductVariants.find((item) => item.meter?.meterId === meterId)?.meter?.name,
      data: getChartValues(meterUsageData, billingCycle, selectedInstanceKeyValue),
      backgroundColor: barColors[index] ?? barColors[0],
    };
  });
}

export function buildPerUnitChartData(
  chartLabels: string[],
  metersData: DynamicInvoiceMeasurementChartMeter,
  perUnitLineItemProductVariants: LineItemProductVariant[],
  billingCycle: Period,
  selectedInstanceKeyValue: string | undefined)
  : ChartData<'bar'> {
  return {
    labels: chartLabels,
    datasets: generateDatasets(metersData, perUnitLineItemProductVariants, billingCycle, selectedInstanceKeyValue),
  };
}

export function buildTiersChartData(chartLabels: string[], chartData: number[], datasetLabel?: string): ChartData<'bar'> {
  return {
    labels: chartLabels,
    datasets: [
      {
        label: datasetLabel,
        data: chartData,
        backgroundColor: newVyPalette.primary.main,
      },
    ],
  };
}

export function getChartLabels(billingCycle: Period) {
  const dates = getPeriodDaysWithUtc(billingCycle);

  return dates.map((date) => getFormattedDate(date.toDate()));
}

export function getChartValues(chartData: DynamicInvoiceMeasurementChartMeterUsage, billingCycle: Period, selectedInstanceKeyValue: string | undefined) {
  const fullBillingCycleMeterUsage = getFullBillingCycleMeterUsage(chartData, billingCycle);

  const sortedMapKeysAsNumbers = getSortedKeysAsNumbers(fullBillingCycleMeterUsage);
  const sortedMapValues = sortedMapKeysAsNumbers.map((key) => getOptionalObjectProperty(fullBillingCycleMeterUsage, key.toString()));
  const purgedMapValues = purge(sortedMapValues);

  return isNullOrUndefined(selectedInstanceKeyValue) 
    ? extract(purgedMapValues, 'totalAmount')
    : buildChartValuesBySelectedInstance(purgedMapValues, selectedInstanceKeyValue);
}

export function buildChartValuesBySelectedInstance(chartData: DynamicInvoiceMeterUsage[], selectedInstanceKeyValue: string) {
  return chartData.map((meterUsage) => {
    if (meterUsage.totalAmount === 0) {
      return 0;
    }

    return meterUsage.amountByInstance?.[selectedInstanceKeyValue] ?? 0;
  });
}

export function getSortedKeysAsNumbers(chartData: DynamicInvoiceMeasurementChartMeterUsage) {
  const mapKeys = keys(chartData);
  const mapKeysAsNumbers = mapKeys.map((key) => Number(key));

  return mapKeysAsNumbers.sort((a, b) => a - b);
}

function getFullBillingCycleMeterUsage(meterUsage: DynamicInvoiceMeasurementChartMeterUsage, billingCycle: Period) {
  const periodDays = getPeriodDaysWithUtc(billingCycle);
  const copiedMeterUsage = { ...meterUsage };

  periodDays.forEach((periodDay) => {
    const isMeterUsageDaysContainsPeriodDay = getIsMeterUsageDaysContainsPeriodDay(meterUsage, periodDay);

    const dateKey = periodDay.valueOf();
    if (!isMeterUsageDaysContainsPeriodDay) {
      copiedMeterUsage[dateKey] = {
        totalAmount: 0,
      };
    }
  });

  return copiedMeterUsage;
}

function getIsMeterUsageDaysContainsPeriodDay(meterUsage: DynamicInvoiceMeasurementChartMeterUsage, periodDay: Dayjs) {
  return Object.keys(meterUsage).some(stringifiedDateInMilliseconds => {
    const dateInMilliseconds = parseInt(stringifiedDateInMilliseconds);
    const meterUsageDay = dayjs(dateInMilliseconds).utc(false);
    const meterUsageDayPeriod = getDayPeriodFromDay(meterUsageDay);

    return isDateBetween(periodDay.toDate(), meterUsageDayPeriod);
  });
}

function getDayPeriodFromDay(day: Dayjs) {
  const startOfDay = day.startOf('day');
  const endOfDay = day.endOf('day');

  return buildPeriod(startOfDay.toDate(), endOfDay.toDate());
}